import { type TimeSlotWithExtras } from "@src/appV2/Shifts/types";
import { useCallback } from "react";

import { useUpdateDefaultShiftTimes } from "../../api/useUpdateDefaultShiftTimes";
import { APPLY_TO_OPTIONS, type EditTimeResult } from "./types";
import { logShiftTimeEditSaved } from "./utils/logShiftTimeEditSaved";

interface UseSaveEditedShiftTimeParams {
  workplaceId: string;
  /** Logged-in user id, recorded on the save event. */
  workplaceUserId?: string;
  qualification: string;
  timeSlot: TimeSlotWithExtras;
  timezone: string;
  /**
   * Applies the edited times to the caller's local draft (form) state. Called
   * for every save, regardless of the chosen scope.
   */
  onApplyTimes: (times: { start: Date; end: Date }) => void;
}

/**
 * Shared handler for the `EditShiftTimeDialog` `onSave` used by the shift-posting
 * flows (inline posting form and Add Shift panel). It logs the edit, applies the
 * new times to the caller's draft, and — unless the user kept the change to just
 * this shift — persists it as the slot/qualification default.
 *
 * Orientation ("ori") shifts persist their default under the synthetic slot; the
 * Add Shift panel never selects "ori" so that branch is inert there.
 */
export function useSaveEditedShiftTime(params: UseSaveEditedShiftTimeParams) {
  const { workplaceId, workplaceUserId, qualification, timeSlot, timezone, onApplyTimes } = params;
  const { mutateAsync: updateDefaultShiftTimes } = useUpdateDefaultShiftTimes();

  return useCallback(
    async (result: EditTimeResult) => {
      logShiftTimeEditSaved({
        workplaceId,
        workplaceUserId,
        timeSlot,
        qualification,
        applyTo: result.applyTo,
      });

      onApplyTimes({ start: result.start, end: result.end });

      if (result.applyTo === APPLY_TO_OPTIONS.THIS_SHIFT_ONLY) {
        return;
      }

      if (timeSlot === "ori") {
        await updateDefaultShiftTimes({
          workplaceId,
          timeSlot: "ori",
          startTime: result.start,
          endTime: result.end,
          timezone,
        });
        return;
      }

      const isApplyToAll = result.applyTo === APPLY_TO_OPTIONS.ALL_SHIFTS;
      await updateDefaultShiftTimes({
        workplaceId,
        timeSlot,
        startTime: result.start,
        endTime: result.end,
        qualification: isApplyToAll ? undefined : qualification,
        currentQualification: isApplyToAll ? qualification : undefined,
        timezone,
      });
    },
    [
      workplaceId,
      workplaceUserId,
      qualification,
      timeSlot,
      timezone,
      onApplyTimes,
      updateDefaultShiftTimes,
    ]
  );
}
